import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, LogOut } from "lucide-react";
import { toast } from "sonner";
import { LogoMark } from "@/components/site/Logo";
import { StatsGrid, SubscribersPanel, OrdersPanel } from "@/components/admin/AdminPanels";
import { GelatoPanel } from "@/components/admin/GelatoPanel";
import { useAuth } from "@/context/AuthContext";
import { adminApi } from "@/lib/api";

const TABS = [
  { id: "subscribers", label: "Iscritti" },
  { id: "orders", label: "Ordini" },
  { id: "catalog", label: "Catalogo Gelato" },
];

const get = (url, params) => adminApi.get(url, { params }).then((r) => r.data);

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("subscribers");
  const [source, setSource] = useState("all");
  const stats = useQuery({ queryKey: ["admin-stats"], queryFn: () => get("/admin/stats") });
  const subs = useQuery({ queryKey: ["admin-subs", source], queryFn: () => get("/admin/subscribers", { source }) });
  const orders = useQuery({ queryKey: ["admin-orders"], queryFn: () => get("/admin/orders") });
  const queryClient = useQueryClient();

  const shipOrder = async (id, tracking) => {
    try {
      await adminApi.post(`/admin/orders/${id}/ship`, { tracking });
      toast.success("Ordine segnato come spedito", { description: "Il cliente lo vede subito nel suo profilo." });
      await queryClient.invalidateQueries({ queryKey: ["admin-orders"] });
    } catch {
      toast.error("Non sono riuscito ad aggiornare l'ordine.");
    }
  };

  const sendGelato = async (id) => {
    try {
      const { data } = await adminApi.post(`/admin/gelato/orders/${id}/send`);
      const state = data.gelato?.state;
      if (state === "sent" || state === "demo") toast.success("Ordine inviato a Gelato");
      else toast.error(data.gelato?.error || "Invio a Gelato non riuscito");
      await queryClient.invalidateQueries({ queryKey: ["admin-orders"] });
    } catch {
      toast.error("Invio a Gelato non riuscito.");
    }
  };

  const exportCsv = async () => {
    try {
      const { data } = await adminApi.get("/admin/subscribers/export", { params: { source }, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sfumo-iscritti-${source}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export non riuscito, riprova.");
    }
  };

  return (
    <div className="min-h-screen" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 flex h-16 items-center justify-between border-b border-line bg-ink/85 px-5 backdrop-blur-xl sm:px-8 lg:px-12">
        <Link to="/" className="flex items-center gap-2.5" data-testid="admin-home-link">
          <LogoMark className="h-8 w-8" />
          <span className="font-display text-sm font-black tracking-[0.2em]">SFUMO</span>
          <span className="hidden font-mono text-[10px] uppercase tracking-[0.25em] text-smoke sm:inline">/ dashboard</span>
        </Link>
        <div className="flex items-center gap-3">
          <span className="hidden font-mono text-[11px] text-smoke md:inline" data-testid="admin-user-email">{user.email}</span>
          <Link to="/" className="flex h-10 items-center gap-2 border border-line px-3 font-mono text-[11px] uppercase tracking-[0.2em] hover:border-acid">
            <ExternalLink className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Sito</span>
          </Link>
          <button onClick={logout} data-testid="admin-logout-button" className="flex h-10 items-center gap-2 border border-line px-3 font-mono text-[11px] uppercase tracking-[0.2em] hover:border-acid">
            <LogOut className="h-3.5 w-3.5" /> Esci
          </button>
        </div>
      </header>

      <main className="px-5 py-12 sm:px-8 lg:px-12">
        <h1 className="font-display text-4xl font-black uppercase leading-none tracking-tight sm:text-5xl">
          Ciao <span className="text-acid">{user.name}.</span>
        </h1>
        <p className="mt-3 text-bone/60">Ecco come stanno andando la crew, la lista di InkMaster e sfushop.</p>

        <div className="mt-10">
          <StatsGrid stats={stats.data} />
        </div>

        <div className="mt-12 flex gap-1 border-b border-line">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`-mb-px border-b-2 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.25em] transition-colors ${tab === t.id ? "border-acid text-bone" : "border-transparent text-smoke hover:text-bone"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "subscribers" && <SubscribersPanel source={source} setSource={setSource} rows={subs.data} loading={subs.isLoading} onExport={exportCsv} />}
          {tab === "orders" && <OrdersPanel rows={orders.data} loading={orders.isLoading} hiddenCount={stats.data?.hidden_abandoned} onShip={shipOrder} onSendGelato={sendGelato} />}
          {tab === "catalog" && <GelatoPanel />}
        </div>
      </main>
    </div>
  );
}
